'use client';

import { useTranslations } from 'next-intl';
import type { SeverityChangeRequest } from '@terra/shared-contracts-findings';
import { Box, HStack } from '@terra/ui-styled-system';
import { MetaDefault } from '@terra/ui-design-system';
import { PendingSeverityChangePill } from './findings-table/PendingSeverityChangePill';

type SeverityChangeRequestHeaderPillProps = {
  severityChangeRequest: SeverityChangeRequest;
  dateFormatter: {
    fromString: (date: string) => string;
  };
  onScrollToCard: () => void;
};

export const SEVERITY_CHANGE_REQUEST_CARD_ELEMENT_ID =
  'severity-change-request-card';

export function scrollToSeverityChangeRequestCard() {
  const element = document.getElementById(
    SEVERITY_CHANGE_REQUEST_CARD_ELEMENT_ID,
  );

  element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function SeverityChangeRequestHeaderPill({
  severityChangeRequest,
  dateFormatter,
  onScrollToCard,
}: SeverityChangeRequestHeaderPillProps) {
  const t = useTranslations(
    'designSystem.findings.findingDrawer.tabs.overview.severityChangeRequest',
  );

  if (severityChangeRequest.status === 'Pending') {
    return (
      <PendingSeverityChangePill
        proposedSeverity={severityChangeRequest.toSeverity}
        label={t('headerPendingPill', {
          severity: severityChangeRequest.toSeverity,
        })}
        onClick={onScrollToCard}
      />
    );
  }

  if (severityChangeRequest.status === 'Approved') {
    const reviewedAt = dateFormatter.fromString(
      severityChangeRequest.reviewedAt instanceof Date
        ? severityChangeRequest.reviewedAt.toISOString()
        : String(severityChangeRequest.reviewedAt),
    );

    return (
      <HStack
        gap="1"
        alignItems="center"
        px="2.5"
        py="0.5"
        borderRadius="full"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="border.neutral.subtle"
        flexShrink={0}
      >
        <MetaDefault color="fg.neutral.muted">
          {t('headerApprovedPrefix')}
        </MetaDefault>
        <Box as="span" textDecoration="line-through">
          <MetaDefault color="fg.neutral.muted">
            {severityChangeRequest.fromSeverity}
          </MetaDefault>
        </Box>
        <MetaDefault color="fg.neutral.muted">
          {t('headerApprovedDate', { date: reviewedAt })}
        </MetaDefault>
      </HStack>
    );
  }

  return null;
}
